import dayjs from 'dayjs';
import { makeAutoObservable } from 'mobx';
import { isCoexistableTeachingBlockCard, TeachingBlockCardType } from '../../components/TeachingBlockGrid/types';
import { CardAvailability, ConflictPlacementFilter } from '../DayAvailabilityStore';
import type { TeachingBlockStore } from './TeachingBlockStore';

/**
 * Berechnet verbundene Klassen-Komponenten aus Lesson-Gruppen.
 * Lessons mit gemeinsamen Klassen werden zusammengeführt.
 * Lessons ohne Überlappung bleiben eigenständige Gruppen.
 */
function buildConnectedComponents(lessonGroups: Set<string>[]): Set<string>[] {
  const components: Set<string>[] = [];
  for (const group of lessonGroups) {
    const overlapping = components.filter((c) => [...group].some((uuid) => c.has(uuid)));
    if (overlapping.length === 0) {
      components.push(new Set(group));
    } else {
      const merged = new Set<string>([...group, ...overlapping.flatMap((c) => [...c])]);
      overlapping.forEach((c) => components.splice(components.indexOf(c), 1));
      components.push(merged);
    }
  }
  return components;
}

/**
 * Konflikt-Erkennung der TeachingBlock-Planung (Phase 7.2), an
 * `TimetableVersion/ConflictStore` orientiert.
 *
 * Quelle der Wahrheit ist `cardConflicts: Map<cardUuid, CardAvailability>` – die
 * Konflikte hängen NICHT mehr am Kartenobjekt (`card.warnings` entfällt). Bei
 * gesplitteten Epochen werden die Konflikte unter der **Parent-UUID** abgelegt
 * (die VirtualCards verweisen via `parentUuid` darauf).
 *
 * Die Anzeige (Badge/Tooltip/Liste) wird daraus über
 * `TeachingBlockStore.getWarningsForCard(uuid)` abgeleitet.
 */
export class TeachingBlockConflictStore {
  public cardConflicts: Map<string, CardAvailability> = new Map();

  constructor(private root: TeachingBlockStore) {
    makeAutoObservable(this);
  }

  /**
   * Connected Components der Klassen aus allen Platzhalter-Lessons des SubjectContainers.
   * Lessons mit gemeinsamen Klassen werden zusammengeführt; disjunkte Lessons
   * bleiben eigene Gruppen.
   *
   * Datenquelle: `root.placeholderLessonGroups` (via SubjectContainerPlaceholderLessons-Query,
   * gefiltert nach SubjectContainer-UUID + Schuljahr-UUID).
   */
  private get _placeholderConflictComponents(): Set<string>[] {
    return buildConnectedComponents(this.root.placeholderLessonGroups);
  }

  /**
   * Konflikt-Filter für eine Karte, der Klassen-Scoping (Placeholder) **und** die
   * Wahlfach-Regeln vereint. Die drei Fälle brauchen bewusst unterschiedliche
   * Behandlung – ein simples `placeholder && coexist` wäre falsch, weil das
   * Klassen-Scoping (Einzel-Komponente → Klassenkonflikt aus) sonst die Mischung
   * fälschlich als konfliktfrei durchwinkt:
   *
   * - **Zwei Wahlfächer** → nie ein Konflikt (gewollter Stapel), unabhängig vom Scoping.
   * - **Mischung** (genau eine Karte ist ein Wahlfach) → immer ein Konflikt: ein
   *   Wahlfach kann sich mit einem normalen Fach keine Zeile teilen. Überschreibt das
   *   Klassen-Scoping, damit die Mischung auch in Einzel-Komponenten sichtbar wird.
   *   Symmetrisch – egal welche der beiden Karten das Wahlfach ist.
   * - **Zwei normale Karten** → unverändertes Placeholder-Verhalten (Klassen-Scoping).
   *
   * Gefiltert wird ausschließlich die Belegung durch Fremdkarten – Verfügbarkeiten
   * (`blocked`, `maybe`) laufen am Filter vorbei und gelten unverändert weiter.
   *
   * Der Filter fällt NIE auf „jede Fremdkarte zählt“ zurück: gibt es keine passende
   * Placeholder-Komponente, werden Konflikte unterdrückt – für Klassen, Lehrer UND Räume.
   */
  public getPlaceholderFilterForCard(card: TeachingBlockCardType): ConflictPlacementFilter {
    const placeholderFilter = this._placeholderConflictFilter(card);
    const pickedCoexistable = isCoexistableTeachingBlockCard(card);

    return (params) => {
      const placedCard = this.root.cardStore.getParentCard(params.placedCardUuid);
      const placedCoexistable = !!placedCard && isCoexistableTeachingBlockCard(placedCard);

      if (pickedCoexistable && placedCoexistable) return false; // zwei Wahlfächer → Stapel
      if (pickedCoexistable !== placedCoexistable) return true; // Mischung → immer Konflikt

      return placeholderFilter(params); // zwei normale Karten → Placeholder-Scoping
    };
  }

  /**
   * Nur die Wahlfach-Unterdrückung, ohne das Klassen-Scoping des Placeholder-Filters.
   * Für den Auto-Fit-Scan (`_fitEndBeforeNextCard`), der ganz ohne Filter prüft: ein
   * Wahlfach soll vor einem normalen Fach stoppen (Konflikt zählt), ein anderes Wahlfach
   * aber durchlassen (Stapel). Für normale Karten `undefined` → unverändertes Verhalten.
   */
  public getCoexistFilterForCard(card: TeachingBlockCardType): ConflictPlacementFilter | undefined {
    if (!isCoexistableTeachingBlockCard(card)) return undefined;

    return ({ placedCardUuid }) => {
      const placedCard = this.root.cardStore.getParentCard(placedCardUuid);
      return !placedCard || !isCoexistableTeachingBlockCard(placedCard);
    };
  }

  private _placeholderConflictFilter(card: TeachingBlockCardType): ConflictPlacementFilter {
    const components = this._placeholderConflictComponents;

    // Passende Komponente = größte Schnittmenge mit den Kartenklassen (Komponenten sind
    // disjunkt, aber eine Karte kann Klassen aus mehreren Lessons kombinieren → die mit der
    // größten Überlappung gewinnt). Gibt es keine Überlappung, bleibt `component` undefined.
    const component = components
      .map((c) => ({ c, overlap: card.classes.reduce((n, { uuid }) => n + (c.has(uuid) ? 1 : 0), 0) }))
      .filter(({ overlap }) => overlap > 0)
      .sort((a, b) => b.overlap - a.overlap)[0]?.c;

    return ({ resourceType, resourceUuid, placedCardUuid }) => {
      // Die Komponente ist die Klassen-GRUPPE der gepickten Karte (bei nicht klassenübergreifenden
      // Karten = nur sie selbst). Hat die Karte keinen Placeholder-Lesson-Bezug, zählt kein Konflikt
      // („nur rot, wenn es eine Placeholder-Lesson gibt“) – kein Rückfall auf „jede Fremdkarte zählt“.
      if (!component) return false;

      // Klasse: überlappt eine Fremdkarte eine Klasse der Gruppe, ist es ein echter Konflikt
      // (dieselben Schüler) – auch bei nicht klassenübergreifenden Karten („Konflikt mit sich
      // selbst“). resourceUuid ist die Klassen-UUID → direkt gegen die Gruppe prüfen.
      if (resourceType === 'class') return component.has(resourceUuid);

      // Klassengruppe/Lehrer/Raum/Fach: die Fremdkarte muss eine Klasse der Gruppe teilen. Für
      // Klassengruppen ist das der sichtbare Überlappungs-Konflikt; Lehrer/Raum-Doppelbelegungen
      // sind im FE ohnehin unterbunden und tauchen praktisch nie auf.
      const placedCard = this.root.cardStore.getParentCard(placedCardUuid);
      return !!placedCard && placedCard.classes.some(({ uuid }) => component.has(uuid));
    };
  }

  /** Alle konfliktrelevanten Karten: platzierte + Parents gesplitteter Karten (dedupliziert). */
  private _conflictCards(): TeachingBlockCardType[] {
    const virtualParents = this.root.virtualCards
      .map(({ parentUuid }) => this.root.cardStore.getParentCard(parentUuid))
      .filter(Boolean) as TeachingBlockCardType[];

    const map = new Map<string, TeachingBlockCardType>();
    [...this.root.placedCards, ...virtualParents].forEach((c) => map.set(c.uuid, c));
    return Array.from(map.values());
  }

  /** Tagesliste (inklusive) für den Datumsbereich einer Karte. */
  private _datesForCard(card: TeachingBlockCardType): Date[] {
    const startDay = dayjs(card.startDate).startOf('day');
    const endDay = dayjs(card.endDate).startOf('day');
    const allDays = endDay.diff(startDay, 'days');
    const days: Date[] = [];
    for (let i = 0; i <= allDays; i++) {
      days.push(startDay.add(i, 'days').toDate());
    }
    return days;
  }

  private _computeForCard(card: TeachingBlockCardType) {
    if (!card.startDate || !card.endDate) return;
    const days = this._datesForCard(card);
    const availability = this.root
      .getAvailabilityStore()
      ?.checkResourceConflicts(days, this.root.cardStore.cardToAvailabilityCard(card), {
        // Coexist-bewusster Filter: zwei gestapelte Wahlfächer erzeugen keinen
        // Dauer-Konflikt, eine Mischung Wahlfach↔normal aber sehr wohl (symmetrisch auf
        // beiden Karten). Konsistent zum Placement-Check in placeCard.
        shouldCountPlacement: this.getPlaceholderFilterForCard(card),
      });
    if (availability && availability.size > 0) {
      this.cardConflicts.set(card.uuid, availability);
    }
  }

  /** Vollständiger Neuaufbau (entspricht dem alten `_findConflicts`). */
  public setConflicts() {
    this.removeConflicts();
    this._conflictCards().forEach((card) => this._computeForCard(card));
  }

  /** Inkrementelles Update einzelner Karten (für 7.4 / Performance). */
  public updateConflictsForCards(cardUuids: Set<string>) {
    cardUuids.forEach((uuid) => this.cardConflicts.delete(uuid));
    cardUuids.forEach((uuid) => {
      const card = this.root.cardStore.getParentCard(uuid);
      if (card) this._computeForCard(card);
    });
  }

  /**
   * Karten, die mit `cardUuid` mindestens eine Ressource teilen UND deren
   * Datumsbereich sich überschneidet. Basis für inkrementelle Updates (7.4).
   */
  public _getAffectedCardsForCard(cardUuid: string): Set<string> {
    const affected = new Set<string>();
    const card = this.root.cardStore.getParentCard(cardUuid);
    if (!card?.startDate || !card?.endDate) return affected;

    const myAvail = this.root.cardStore.cardToAvailabilityCard(card);
    const myResources = new Set<string>([
      ...myAvail.teachers,
      ...myAvail.classes,
      ...myAvail.rooms,
      ...myAvail.classDivisionGroups,
      myAvail.subject,
    ]);

    const myStart = dayjs(card.startDate).startOf('day');
    const myEnd = dayjs(card.endDate).startOf('day');

    this._conflictCards().forEach((other) => {
      if (other.uuid === cardUuid || !other.startDate || !other.endDate) return;

      // Datumsüberschneidung (inklusive)
      const otherStart = dayjs(other.startDate).startOf('day');
      const otherEnd = dayjs(other.endDate).startOf('day');
      const datesOverlap = myStart.isSameOrBefore(otherEnd, 'day') && otherStart.isSameOrBefore(myEnd, 'day');
      if (!datesOverlap) return;

      const otherAvail = this.root.cardStore.cardToAvailabilityCard(other);
      const sharesResource = [
        ...otherAvail.teachers,
        ...otherAvail.classes,
        ...otherAvail.rooms,
        ...otherAvail.classDivisionGroups,
        otherAvail.subject,
      ].some((r) => myResources.has(r));

      if (sharesResource) affected.add(other.uuid);
    });

    affected.add(cardUuid);
    return affected;
  }

  public removeConflicts() {
    this.cardConflicts = new Map();
  }
}
